/**
 * onnxruntime-web's wasm builds disagree on which kernels they ship:
 *
 *   - `onnxruntime-web/webgpu`  → ort-wasm-simd-threaded.asyncify.wasm
 *       Native WebGPU EP for every contrib op the privacy-filter uses
 *       (MatMulNBits, QMoE, GatherBlockQuantized, RotaryEmbedding,
 *       SkipLayerNorm, SkipSimplifiedLayerNormalization,
 *       GroupQueryAttention). Asyncify-style stack switching for clean
 *       async GPU dispatch. **CPU contrib kernels are stripped** — if
 *       the partitioner needs to spill to wasm, ops like
 *       GatherBlockQuantized fail with "kernel not found".
 *
 *   - `onnxruntime-web/all`     → ort-wasm-simd-threaded.jsep.wasm
 *       Older JSEP (no asyncify) for the WebGPU side — covers
 *       MatMulNBits, GatherBlockQuantized, SkipLayerNorm; QMoE +
 *       RotaryEmbedding spill to CPU EP. **Full CPU contrib kernels
 *       are present**, so the spill works.
 *
 * So: prefer asyncify when WebGPU is available (best perf), fall back
 * to /all when it isn't (so the model still loads on CPU-only browsers
 * instead of crashing on GatherBlockQuantized).
 */

// eslint-disable-next-line import/no-unresolved
import asyncifyMjsUrl from "../node_modules/onnxruntime-web/dist/ort-wasm-simd-threaded.asyncify.mjs?url";
// eslint-disable-next-line import/no-unresolved
import asyncifyWasmUrl from "../node_modules/onnxruntime-web/dist/ort-wasm-simd-threaded.asyncify.wasm?url";
// eslint-disable-next-line import/no-unresolved
import jsepMjsUrl from "../node_modules/onnxruntime-web/dist/ort-wasm-simd-threaded.jsep.mjs?url";
// eslint-disable-next-line import/no-unresolved
import jsepWasmUrl from "../node_modules/onnxruntime-web/dist/ort-wasm-simd-threaded.jsep.wasm?url";

// Both subpath exports re-expose `onnxruntime-common`, so the type
// surface is identical — pick either for the namespace type.
export type OrtNamespace = typeof import("onnxruntime-web/webgpu");

export type OrtFlavor = "webgpu-asyncify" | "all-jsep";

export interface LoadedOrt {
  ort: OrtNamespace;
  flavor: OrtFlavor;
  /** EP list to hand to InferenceSession.create. Ordered: try first then fall back. */
  executionProviders: readonly string[];
  /** Effective wasm thread count we configured. */
  numThreads: number;
}

let cached: Promise<LoadedOrt> | null = null;

/**
 * Lazy-load onnxruntime-web with the right wasm flavor. Memoised — repeat
 * calls return the same promise. Safe to await from any async context.
 */
export function loadOrt(): Promise<LoadedOrt> {
  if (!cached) cached = doLoad();
  return cached;
}

async function doLoad(): Promise<LoadedOrt> {
  const flavor = await pickFlavor();
  const ort: OrtNamespace = flavor === "webgpu-asyncify"
    ? await import("onnxruntime-web/webgpu")
    : await import("onnxruntime-web/all");

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const env = (ort as any).env;
  try {
    // Vite's dependency optimizer flattens the ORT entry point into a
    // single JS file but doesn't bring the .wasm asset along, so the
    // loader's "fetch the wasm sibling" strategy receives the SPA's
    // index.html instead and trips
    //   "expected magic word 00 61 73 6d, found 3c 21 64 6f"
    // We sidestep that by handing ORT explicit URLs to the .mjs / .wasm
    // pair (the per-key form, not the string-base-directory form, which
    // sidesteps the `?import` query Vite appends and the HTML wrap that
    // follows).
    env.wasm.wasmPaths = flavor === "webgpu-asyncify"
      ? { mjs: asyncifyMjsUrl, wasm: asyncifyWasmUrl }
      : { mjs: jsepMjsUrl, wasm: jsepWasmUrl };

    env.wasm.numThreads = pickNumThreads();
  } catch {
    /* env may not be writable on some builds */
  }

  const executionProviders = flavor === "webgpu-asyncify"
    ? (["webgpu", "wasm"] as const)
    // /all routes WebGPU through JSEP; spillover lands on the CPU
    // contrib kernels in ort-wasm-simd-threaded.jsep.wasm.
    : (["wasm"] as const);

  return {
    ort,
    flavor,
    executionProviders,
    numThreads: env?.wasm?.numThreads ?? 1,
  };
}

async function pickFlavor(): Promise<OrtFlavor> {
  const nav = navigator as unknown as {
    gpu?: { requestAdapter: () => Promise<unknown> };
  };
  if (!nav.gpu) return "all-jsep";
  try {
    const adapter = await nav.gpu.requestAdapter();
    return adapter ? "webgpu-asyncify" : "all-jsep";
  } catch {
    return "all-jsep";
  }
}

/**
 * Multi-threaded wasm via SharedArrayBuffer is OFF by default. The page
 * IS cross-origin isolated (COOP=same-origin, COEP=credentialless) so
 * SAB is available — but ORT-Web 1.25's threaded asyncify build hangs
 * at session create on the privacy-filter model when numThreads > 1.
 * Until upstream fixes it, leave threading opt-in via a localStorage
 * key. The flag persists; a user that wants to experiment sets
 *   localStorage.redact_wasm_threads = "8"
 * and reloads.
 */
function pickNumThreads(): number {
  let n = 1;
  try {
    const v = Number(localStorage.getItem("redact_wasm_threads"));
    if (Number.isFinite(v) && v >= 1 && v <= 16) n = Math.floor(v);
  } catch { /* ignore */ }
  if (n > 1 && (typeof self === "undefined"
    || !(self as unknown as { crossOriginIsolated?: boolean }).crossOriginIsolated)) {
    n = 1;
  }
  return n;
}
